import { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Button,
    SegmentedControl,
    Select,
    TextInput,
    Loader,
} from '@mantine/core';
import IconSend from '../components/Icon/IconSend';

const FuzzySugeno = () => {
    const [data, setData] = useState([]);
    const [activeTab, setActiveTab] = useState('numeric');
    const [input, setInput] = useState({ suhu: '', kebisingan: '', pencahayaan: '' });
    const [errors, setErrors] = useState({ suhu: '', kebisingan: '', pencahayaan: '' });
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingSubmit, setLoadingSubmit] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get('https://be-mti-udinus.aemde.uk/hasil-fuzzy');
            setData(res.data);
        } catch (error) {
            console.error('Gagal fetch data:', error);
        } finally {
            setLoading(false);
        }
    };

    const validate = () => {
        let valid = true;
        let newErrors = { suhu: '', kebisingan: '', pencahayaan: '' };

        const suhu = parseFloat(input.suhu);
        const kebisingan = parseFloat(input.kebisingan);
        const pencahayaan = parseFloat(input.pencahayaan);

        if (!(suhu >= 16 && suhu <= 32)) {
            newErrors.suhu = 'Suhu harus antara 16°C hingga 32°C';
            valid = false;
        }
        if (!(kebisingan >= 35 && kebisingan <= 115)) {
            newErrors.kebisingan = 'Kebisingan harus antara 35 dB hingga 115 dB';
            valid = false;
        }
        if (!(pencahayaan >= 150 && pencahayaan <= 550)) {
            newErrors.pencahayaan = 'Pencahayaan harus antara 150 hingga 550 lux';
            valid = false;
        }

        setErrors(newErrors);
        return valid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (activeTab === 'numeric' && !validate()) {
            setMessage('Input tidak valid. Periksa kembali nilai suhu, kebisingan, dan pencahayaan.');
            return;
        }

        const payload = activeTab === 'numeric'
            ? {
                suhu: parseFloat(input.suhu),
                kebisingan: parseFloat(input.kebisingan),
                pencahayaan: parseFloat(input.pencahayaan),
            }
            : input;

        const endpoint = activeTab === 'numeric' ? '/fuzzy' : '/fuzzy-label';

        setLoadingSubmit(true); // ⬅️ Ganti ini
        try {
            await axios.post(`https://be-mti-udinus.aemde.uk${endpoint}`, payload);
            await fetchData();
            setInput({ suhu: '', kebisingan: '', pencahayaan: '' });
            setErrors({ suhu: '', kebisingan: '', pencahayaan: '' });
            setMessage('Data berhasil dikirim!');
        } catch (error) {
            console.error('Gagal kirim data:', error);
            setMessage('Gagal kirim data. Coba lagi.');
        } finally {
            setLoadingSubmit(false); // ⬅️ Dan ini
        }
    };



    const renderInput = () => {
        if (activeTab === 'numeric') {
            return (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 max-w-4xl mx-auto">
                    {['suhu', 'kebisingan', 'pencahayaan'].map((field) => (
                        <TextInput
                            key={field}
                            type="number"
                            label={
                                field === 'suhu'
                                    ? 'Suhu (°C)'
                                    : field === 'kebisingan'
                                        ? 'Kebisingan (dB)'
                                        : 'Pencahayaan (lux)'
                            }
                            placeholder="Masukkan nilai"
                            value={input[field]}
                            onChange={(e) => setInput({ ...input, [field]: e.target.value })}
                            error={errors[field] || false}
                            withAsterisk
                        />
                    ))}
                </div>
            );
        } else {
            return (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 max-w-4xl mx-auto">
                    <Select
                        label="Suhu"
                        placeholder="Pilih Suhu"
                        data={[
                            { value: 'rendah', label: 'Rendah' },
                            { value: 'normal', label: 'Normal' },
                            { value: 'tinggi', label: 'Tinggi' },
                        ]}
                        value={input.suhu}
                        onChange={(value) => setInput({ ...input, suhu: value || '' })}
                    />
                    <Select
                        label="Kebisingan"
                        placeholder="Pilih Kebisingan"
                        data={[
                            { value: 'tenang', label: 'Tenang' },
                            { value: 'agak bising', label: 'Agak Bising' },
                            { value: 'bising', label: 'Bising' },
                        ]}
                        value={input.kebisingan}
                        onChange={(value) => setInput({ ...input, kebisingan: value || '' })}
                    />
                    <Select
                        label="Pencahayaan"
                        placeholder="Pilih Pencahayaan"
                        data={[
                            { value: 'redup', label: 'Redup' },
                            { value: 'agak terang', label: 'Agak Terang' },
                            { value: 'terang', label: 'Terang' },
                        ]}
                        value={input.pencahayaan}
                        onChange={(value) => setInput({ ...input, pencahayaan: value || '' })}
                    />
                </div>
            );
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4 mt-6">
            <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-md overflow-hidden p-6">
                <div className="flex flex-col items-center mb-6">
                    <img
                        src="https://dinus.ac.id/wp-content/uploads/2023/11/LogoUdinus.png"
                        className="w-14 mb-2"
                        alt="Logo"
                    />
                    <p className="text-sm text-gray-600 mt-1">P31.2024.02640 - Ahmad Mursyidi</p>
                    <h1 className="text-2xl font-bold text-gray-800 mt-0">Fuzzy Sugeno System</h1>
                </div>

                <div className="mb-6">
                    <SegmentedControl
                        value={activeTab}
                        onChange={setActiveTab}
                        fullWidth
                        size="sm"
                        radius="md"
                        data={[
                            { label: 'Input Angka', value: 'numeric' },
                            { label: 'Input Label', value: 'label' },
                        ]}
                    />
                </div>

                {renderInput()}

                <div className="text-center mb-11 mt-6">
                    <Button
                        type="button"
                        onClick={handleSubmit}
                        leftIcon={<IconSend size={18} />}
                        color="blue"
                        radius="md"
                        disabled={loadingSubmit}
                    >
                        {loadingSubmit ? 'Mengirim...' : 'Submit'}
                    </Button>

                    {loadingSubmit && <Loader size="sm" className="mx-auto mt-2" />}

                    {/*{loading && <Loader size="sm" className="mx-auto mt-2" />}*/}
                    {message && <p className="mt-2 text-sm text-green-600 font-medium">{message}</p>}
                </div>

                <div className="mt-10 mb-4">
                    <h2 className="text-lg font-semibold text-gray-700">Hasil Perhitungan Terakhir</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm text-left text-gray-500">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-100">
                        <tr>
                            <th className="px-4 py-2">No</th>
                            <th className="px-4 py-2">Suhu</th>
                            <th className="px-4 py-2">Kebisingan</th>
                            <th className="px-4 py-2">Pencahayaan</th>
                            <th className="px-4 py-2">Hasil</th>
                            <th className="px-4 py-2">Timestamp</th>
                        </tr>
                        </thead>
                        <tbody>
                        {loading ? (
                            [...Array(5)].map((_, i) => (
                                <tr key={i} className="animate-pulse bg-white border-b even:bg-gray-50">
                                    {[...Array(6)].map((_, j) => (
                                        <td key={j} className="px-4 py-2">
                                            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            data.map((row, index) => (
                                <tr
                                    key={row.id || index}
                                    className="bg-white border-b hover:bg-gray-50 even:bg-gray-50"
                                >
                                    <td className="px-4 py-2 font-medium text-gray-900">{index + 1}</td>
                                    <td className="px-4 py-2">{row.suhu}</td>
                                    <td className="px-4 py-2">{row.kebisingan}</td>
                                    <td className="px-4 py-2">{row.pencahayaan}</td>
                                    <td className="px-4 py-2 font-semibold text-blue-600">{row.hasil}</td>
                                    <td className="px-4 py-2 text-xs text-gray-500">
                                        {formatTimestamp(row.timestamp)}
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>


            </div>
        </div>
    );
};

export default FuzzySugeno;

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

const formatTimestamp = (timestamp) => {
    const now = dayjs();
    const date = dayjs(timestamp);

    if (now.diff(date, 'day') >= 1) {
        return date.format('YYYY-MM-DD HH:mm');
    } else {
        return date.fromNow();
    }
};
